import { useState, useCallback, useMemo, useEffect } from 'react';

/**
 * Custom hook for managing UI state
 * Single Responsibility: UI state management
 */
export const useUIState = () => {
  const [feedback, setFeedback] = useState('');
  const [lastMove, setLastMove] = useState(null); // { from, to } highlighted on the board
  const [moveBadge, setMoveBadge] = useState(null); // { square, type: 'correct' | 'wrong' }
  const [showCelebration, setShowCelebration] = useState(false);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [possibleMoves, setPossibleMoves] = useState([]);
  const [showAddLineModal, setShowAddLineModal] = useState(false);
  const [congratulationsData, setCongratulationsData] = useState(null);
  
  // Initialize sound enabled from localStorage
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const savedSetting = localStorage.getItem('chessTrainerSoundEnabled');
    return savedSetting !== null ? savedSetting === 'true' : true;
  });

  // Save sound setting to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('chessTrainerSoundEnabled', soundEnabled.toString());
  }, [soundEnabled]);

  const clearSelection = useCallback(() => {
    setSelectedSquare(null);
    setPossibleMoves([]);
  }, []);

  const clearGameState = useCallback(() => {
    clearSelection();
    setMoveBadge(null);
  }, [clearSelection]);

  const clearCelebration = useCallback(() => {
    setShowCelebration(false);
    setCongratulationsData(null);
  }, []);

  const resetUI = useCallback(() => {
    setFeedback('');
    clearGameState();
    setLastMove(null);
    clearCelebration();
  }, [clearGameState, clearCelebration]);

  return useMemo(() => ({
    // State
    feedback,
    lastMove,
    moveBadge,
    showCelebration,
    selectedSquare,
    possibleMoves,
    showAddLineModal,
    congratulationsData,
    soundEnabled,
    
    // Setters
    setFeedback,
    setLastMove,
    setMoveBadge,
    setShowCelebration,
    setSelectedSquare,
    setPossibleMoves,
    setShowAddLineModal,
    setCongratulationsData,
    setSoundEnabled,
    
    // Actions
    clearSelection,
    clearGameState,
    clearCelebration,
    resetUI
  }), [
    feedback, lastMove, moveBadge, showCelebration, selectedSquare, possibleMoves,
    showAddLineModal, congratulationsData, soundEnabled,
    setFeedback, setLastMove, setMoveBadge, setShowCelebration, setSelectedSquare,
    setPossibleMoves, setShowAddLineModal, setCongratulationsData, setSoundEnabled,
    clearSelection, clearGameState, clearCelebration, resetUI
  ]);
};
